import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "./SidebarContext";
import { useT } from "@/i18n/I18nContext";
import { ChevronDown, ChevronRight as Chevron, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import {
  LayoutDashboard,
  Users,
  HeartPulse,
  Building2,
  ShieldCheck,
  Tags,
  ScrollText,
  Activity,
  ClipboardList,
  FilePlus2,
  ListChecks,
  Stethoscope,
  ClipboardCheck,
  HomeIcon,
  Briefcase,
  PhoneCall,
  PhoneIncoming,
  Siren,
  CalendarClock,
  CheckCircle2,
  GitBranch,
  Radio,
  Gauge,
  BellRing,
  SlidersHorizontal,
  Download,
  MapIcon,
  Truck,
  Video,
  ShieldAlert,
  ListTodo,
  UserSquare2,
  Workflow,
  Crown,
  Layers,
  FileBarChart,
  Search,
} from "lucide-react";

const ADMIN_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-dashboard" },
  { to: "/users", label: "User Management", icon: Users, testId: "nav-users" },
  { to: "/patients", label: "Patient Management", icon: HeartPulse, testId: "nav-patients" },
  { to: "/facilities", label: "Facility Management", icon: Building2, testId: "nav-facilities" },
  { to: "/roles", label: "Roles & Permissions", icon: ShieldCheck, testId: "nav-roles" },
  { to: "/lookups", label: "Lookups", icon: Tags, testId: "nav-lookups" },
  { to: "/protocols", label: "Clinical Protocols", icon: GitBranch, testId: "nav-protocols" },
  { to: "/rpm/thresholds", label: "RPM Thresholds", icon: SlidersHorizontal, testId: "nav-rpm-thresholds" },
  { to: "/sms", label: "SMS Console", icon: PhoneCall, testId: "nav-sms" },
  { to: "/reports", label: "Reports & Exports", icon: Download, testId: "nav-reports" },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, testId: "nav-audit" },];

const RPM_NAV = {
  section: "RPM Command Center",
  items: [
    { to: "/rpm", label: "Command Center", icon: Radio, exact: true, testId: "nav-rpm" },
    { to: "/rpm/alerts", label: "Active Alerts", icon: BellRing, testId: "nav-rpm-alerts" },
    { to: "/rpm/interventions", label: "Interventions", icon: Gauge, testId: "nav-rpm-interventions" },
  ],
};

const FLEET_NAV = {
  section: "Fleet Operations",
  items: [
    { to: "/fleet", label: "Fleet Dashboard", icon: Briefcase, exact: true, testId: "nav-fleet" },
    { to: "/fleet/map", label: "Live Operations Map", icon: MapIcon, testId: "nav-fleet-map" },
    { to: "/fleet/visits", label: "Visit Board", icon: ClipboardList, testId: "nav-fleet-visits" },
    { to: "/fleet/clinicians", label: "Clinicians", icon: Stethoscope, testId: "nav-fleet-clinicians" },
    { to: "/fleet/vehicles", label: "Vehicles", icon: Truck, adminOnly: true, testId: "nav-fleet-vehicles" },
  ],
};

const TELECONSULT_NAV = {
  section: "Teleconsultation",
  items: [
    { to: "/teleconsult", label: "Consultations", icon: Video, exact: true, testId: "nav-teleconsult" },
  ],
};

const CLINICAL_NAV = {
  section: "Clinical Operations",
  items: [
    { to: "/clinical", label: "Active Patients", icon: UserSquare2, exact: true, testId: "nav-clinical" },
    { to: "/clinical/my-patients", label: "My Patients", icon: Stethoscope, testId: "nav-clinical-mine" },
    { to: "/clinical/high-risk", label: "High-Risk Patients", icon: ShieldAlert, testId: "nav-clinical-highrisk" },
    { to: "/rpm/alerts?status=escalated", label: "RPM Escalations", icon: Siren, testId: "nav-clinical-rpm-esc" },
    { to: "/hotline/sessions?status=follow_up_required", label: "Hotline Follow-Ups", icon: CalendarClock, testId: "nav-clinical-hotline-fu" },
    { to: "/teleconsult", label: "Upcoming Teleconsultations", icon: Video, testId: "nav-clinical-tele" },
    { to: "/fleet/visits", label: "Home Visits", icon: MapIcon, testId: "nav-clinical-visits" },
    { to: "/clinical/tasks", label: "Clinical Tasks", icon: ListTodo, testId: "nav-clinical-tasks" },
  ],
};

const COMMAND_NAV = {
  section: "Command Center",
  items: [
    { to: "/command", label: "Executive Dashboard", icon: Crown, exact: true, testId: "nav-command-exec" },
    { to: "/command/operational", label: "Operational Dashboard", icon: LayoutDashboard, testId: "nav-command-ops" },
    { to: "/command/clinical-safety", label: "Clinical Safety", icon: HeartPulse, testId: "nav-command-clinical" },
    { to: "/command/population", label: "Population Health", icon: Layers, testId: "nav-command-pop" },
    { to: "/command/interventions", label: "Active Interventions", icon: ShieldAlert, testId: "nav-command-int" },
    { to: "/command/sla", label: "SLA Manager", icon: GitBranch, testId: "nav-command-sla" },
    { to: "/command/patient-explorer", label: "Patient Explorer", icon: Search, testId: "nav-command-explorer" },
  ],
};

const ONBOARDING_NAV_BY_ROLE = {
  hospital_coordinator: {
    section: "Hospital Workspace",
    items: [
      { to: "/onboarding", label: "Onboarding Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" },
      { to: "/onboarding/new", label: "Create Request", icon: FilePlus2, testId: "nav-ob-new" },
      { to: "/onboarding/requests?scope=mine", label: "My Requests", icon: ClipboardList, testId: "nav-ob-mine" },
      { to: "/onboarding/requests?status=draft", label: "Drafts", icon: ListChecks, testId: "nav-ob-drafts" },
      { to: "/onboarding/requests?status=information_requested", label: "Information Requested", icon: ListChecks, testId: "nav-ob-info" },
      { to: "/onboarding/requests?status=rejected", label: "Rejected", icon: ListChecks, testId: "nav-ob-rejected" },
      { to: "/onboarding/requests?status=visit_completed", label: "Completed", icon: ListChecks, testId: "nav-ob-completed" },
    ],
  },
  hah_physician: {
    section: "H@H Physician",
    items: [
      { to: "/onboarding", label: "Onboarding Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" },
      { to: "/onboarding/requests?status=under_medical_review", label: "Medical Review Queue", icon: Stethoscope, testId: "nav-ob-queue" },
      { to: "/onboarding/requests?status=information_requested", label: "Information Requested", icon: ListChecks, testId: "nav-ob-info" },
      { to: "/onboarding/requests?status=rejected", label: "Rejected", icon: ListChecks, testId: "nav-ob-rejected" },
    ],
  },
  physician: {
    section: "H@H Physician",
    items: [
      { to: "/onboarding", label: "Onboarding Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" },
      { to: "/onboarding/requests?status=under_medical_review", label: "Medical Review Queue", icon: Stethoscope, testId: "nav-ob-queue" },
    ],
  },
  hah_coordinator: {
    section: "H@H Operations",
    items: [
      { to: "/onboarding", label: "Onboarding Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" },
      { to: "/onboarding/requests?status=medical_approved,under_operational_assessment", label: "Assessment Queue", icon: ClipboardCheck, testId: "nav-ob-assess" },
      { to: "/onboarding/requests?status=operationally_approved", label: "Awaiting Assignment", icon: ListChecks, testId: "nav-ob-await" },
      { to: "/onboarding/requests?status=assigned,visit_in_progress", label: "Assigned Visits", icon: ListChecks, testId: "nav-ob-assigned" },
      { to: "/onboarding/requests?status=visit_completed", label: "Visit Findings", icon: ListChecks, testId: "nav-ob-findings" },
      { to: "/onboarding/requests?status=rejected", label: "Rejected", icon: ListChecks, testId: "nav-ob-rejected" },
      { to: "/care-teams", label: "Care Teams", icon: Users, testId: "nav-care-teams-hahco" },
    ],
  },
  care_coordinator: {
    section: "H@H Operations",
    items: [
      { to: "/onboarding", label: "Onboarding Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" },
      { to: "/onboarding/requests?status=medical_approved,under_operational_assessment", label: "Assessment Queue", icon: ClipboardCheck, testId: "nav-ob-assess" },
      { to: "/onboarding/requests?status=operationally_approved", label: "Awaiting Assignment", icon: ListChecks, testId: "nav-ob-await" },
      { to: "/care-teams", label: "Care Teams", icon: Users, testId: "nav-care-teams-cc" },
    ],
  },
  home_care_team: {
    section: "Field Workspace",
    items: [
      { to: "/onboarding", label: "Visits Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" },
      { to: "/onboarding/requests?status=assigned", label: "Assigned Visits", icon: Briefcase, testId: "nav-ob-assigned" },
      { to: "/onboarding/requests?status=visit_in_progress", label: "In Progress", icon: HomeIcon, testId: "nav-ob-inprogress" },
      { to: "/onboarding/requests?status=visit_completed", label: "Completed Visits", icon: ListChecks, testId: "nav-ob-completed" },
    ],
  },
  hah_nurse: {
    section: "Nurse Hotline",
    items: [
      { to: "/hotline", label: "Hotline Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-hotline-dashboard" },
      { to: "/hotline/new", label: "New Hotline Session", icon: PhoneIncoming, testId: "nav-hotline-new" },
      { to: "/hotline/sessions", label: "Active Sessions", icon: PhoneCall, testId: "nav-hotline-active" },
      { to: "/hotline/sessions?status=decision_pending", label: "Decision Pending", icon: ListChecks, testId: "nav-hotline-decision" },
      { to: "/hotline/sessions?status=escalated", label: "Escalated", icon: Siren, testId: "nav-hotline-escalated" },
      { to: "/hotline/sessions?status=follow_up_required", label: "Follow-Up Required", icon: CalendarClock, testId: "nav-hotline-fu" },
      { to: "/hotline/sessions?status=closed", label: "Closed Sessions", icon: CheckCircle2, testId: "nav-hotline-closed" },
    ],
  },
};


/*
 * R-014 — the port gate. The sidebar still lists every module from the FastAPI
 * prototype, but only these have a module in `backendv2/` (see
 * ../../backendv2/docs/CLAUDE.md → Overview). Anything else routes to an API that
 * doesn't exist on Laravel yet, so it renders greyed and non-interactive rather
 * than 404-ing the user.
 *
 * Adding a module here is the one edit needed to light its nav entries back up.
 */
const PORTED_ROUTES = [
  "/users",
  "/roles",
  "/facilities",
  "/lookups",
  "/patients", // allergies live nested under the patient record
  "/teleconsult",
  "/care-teams",
  "/command",   // read-only aggregation; unported sources render "Not yet available"
  "/protocols",
  "/rpm",       // covers /rpm, /rpm/alerts, /rpm/interventions, /rpm/thresholds
];

function isPorted(to) {
  const path = to.split("?")[0];
  return PORTED_ROUTES.some((p) => path === p || path.startsWith(`${p}/`));
}

/*
 * R-016 — the permission gate. Route prefix → the `{module}.view` slug that must
 * be in `user.permissions` for the entry to appear at all.
 *
 * Only ported routes appear here: an unported module has no slugs in
 * `PermissionCatalog` yet, so there is nothing to check and R-014's "Soon" state
 * applies instead. Role-name gating (the `show*` sets below) stays as the only
 * signal for those.
 *
 * Denied entries are HIDDEN, not greyed — greying says "coming soon", which is
 * the wrong message for "you are not allowed".
 */
const PERMISSION_BY_ROUTE = {
  "/users": "users.view",
  "/roles": "roles.view",
  "/facilities": "facilities.view",
  "/lookups": "lookups.view",
  "/patients": "patients.view",
  "/teleconsult": "teleconsult.view",
  "/care-teams": "care_teams.view",
  "/command": "command_center.view",
  "/protocols": "protocols.view",
  "/rpm": "rpm.view",
  "/audit": "audit.view",
};

function requiredPermission(to) {
  const path = to.split("?")[0];
  const key = Object.keys(PERMISSION_BY_ROUTE).find(
    (p) => path === p || path.startsWith(`${p}/`)
  );
  return key ? PERMISSION_BY_ROUTE[key] : null;
}

/*
 * Fail open when the API didn't serialize a permission list at all.
 *
 * `UserResource` only emits `permissions` when the roles/permissions relation is
 * loaded, so a backend regression that drops the eager-load would otherwise blank
 * the entire sidebar. Menu gating is UX (the API is the real gate — see
 * BUSINESS_RULES "nav visibility ≠ authorization"), so an empty nav is the worse
 * failure. A user with a genuinely empty grant list still gets filtered.
 */
function permissionFilter(user, can) {
  if (!Array.isArray(user?.permissions)) return () => true;
  return (item) => {
    const perm = requiredPermission(item.to);
    return !perm || can(perm);
  };
}

function isItemActive(location, item) {
  const [path, query] = item.to.split("?");
  if (item.exact) {
    if (location.pathname !== path) return false;
    if (!query) return !location.search || location.search === "";
    return location.search.replace(/^\?/, "") === query;
  }
  if (!location.pathname.startsWith(path)) return false;
  if (query) {
    return location.search.replace(/^\?/, "") === query;
  }
  return true;
}

// R-014 — an unported entry keeps its icon, label and testId (e2e still finds it)
// but is inert: no NavLink, no href, not focusable.
function DisabledNavItem({ item, collapsed, t }) {
  const Icon = item.icon;
  const hint = `${t(item.label)} — ${t("not yet migrated to the new backend")}`;
  return (
    <div
      data-testid={item.testId}
      aria-disabled="true"
      title={hint}
      className={`hah-sidebar-item opacity-40 cursor-not-allowed select-none ${collapsed ? "justify-center" : ""}`}
    >
      <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
      {!collapsed && (
        <>
          <span className="truncate">{t(item.label)}</span>
          <span className="ml-auto shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/10 text-white/70">
            {t("Soon")}
          </span>
        </>
      )}
    </div>
  );
}

function NavSection({ title, items: allItems, location, collapsed, expanded, onToggle, t, canSee }) {
  // R-016 — drop entries the user has no permission for, then hide the whole
  // section if that emptied it (no bare header over nothing).
  const items = allItems.filter(canSee);
  if (items.length === 0) return null;

  if (collapsed) {
    return (
      <>
        {items.map((item) => {
          if (!isPorted(item.to)) {
            return <DisabledNavItem key={item.testId + item.to} item={item} collapsed t={t} />;
          }
          const isActive = isItemActive(location, item);
          const Icon = item.icon;
          return (
            <NavLink key={item.testId + item.to} to={item.to} data-testid={item.testId}
              title={t(item.label)}
              className={`hah-sidebar-item justify-center ${isActive ? "active" : ""}`}>
              <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
            </NavLink>
          );
        })}
        <div className="border-b border-white/10 my-2" />
      </>
    );
  }
  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-white/85 hover:text-white text-[13px] uppercase tracking-[0.08em] font-bold"
      >
        <span className="truncate text-left">{t(title)}</span>
        {expanded ? <ChevronDown className="w-3.5 h-3.5 opacity-70 shrink-0" /> : <Chevron className="w-3.5 h-3.5 opacity-70 shrink-0" />}
      </button>
      {expanded && items.map((item) => {
        if (!isPorted(item.to)) {
          return <DisabledNavItem key={item.testId + item.to} item={item} collapsed={false} t={t} />;
        }
        const isActive = isItemActive(location, item);
        const Icon = item.icon;
        return (
          <NavLink key={item.testId + item.to} to={item.to} data-testid={item.testId}
            className={`hah-sidebar-item ${isActive ? "active" : ""}`}>
            <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
            <span className="truncate">{t(item.label)}</span>
          </NavLink>
        );
      })}
    </div>
  );
}


export default function Sidebar() {
  const location = useLocation();
  const { user, can } = useAuth();
  const { collapsed, toggle } = useSidebar();
  const t = useT();
  const role = user?.role_code || "viewer";
  // R-016 — permission filter applied to every section below, on top of the
  // role-name gating. See PERMISSION_BY_ROUTE.
  const canSee = permissionFilter(user, can);

  const onboardingNav = ONBOARDING_NAV_BY_ROLE[role];
  const showAdmin = role === "admin";
  const showHotline = ["hah_nurse", "hah_coordinator", "care_coordinator", "admin"].includes(role);
  const hotlineNav = ONBOARDING_NAV_BY_ROLE["hah_nurse"];
  const showRpm = ["admin", "hah_nurse", "hah_coordinator", "hospital_coordinator", "hah_physician", "physician", "nurse", "care_coordinator", "clinical_director"].includes(role);
  const showFleet = ["admin", "hah_coordinator", "care_coordinator", "clinical_director", "hah_nurse", "hospital_coordinator"].includes(role);
  const showTele = ["admin", "hah_nurse", "hah_coordinator", "hospital_coordinator", "hah_physician", "physician", "nurse", "care_coordinator", "clinical_director"].includes(role);
  const showClinical = ["admin", "hah_physician", "physician", "hah_nurse", "nurse", "care_coordinator", "hah_coordinator", "clinical_director"].includes(role);
  const showCommand = ["admin", "clinical_director", "care_coordinator", "hah_coordinator", "hospital_coordinator", "physician", "hah_physician", "nurse", "hah_nurse"].includes(role);

  // Persist accordion state per section across reloads
  const [expandedSections, setExpandedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hah_sidebar_sections") || "null") || { command: true, clinical: true, admin: true }; }
    catch { return { command: true, clinical: true, admin: true }; }
  });
  const toggleSection = (key) => {
    setExpandedSections((s) => {
      const next = { ...s, [key]: !s[key] };
      try { localStorage.setItem("hah_sidebar_sections", JSON.stringify(next)); } catch (_e) { /* ignore */ }
      return next;
    });
  };
  const ex = (k, def = false) => expandedSections[k] ?? def;

  return (
    <aside
      data-testid="sidebar"
      className={`hah-sidebar fixed inset-y-0 left-0 ${collapsed ? "w-16" : "w-64"} z-30 flex flex-col transition-all duration-200`}
    >
      <div className="px-3 py-4 flex items-center gap-2 border-b border-white/10">
        <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
          <Stethoscope className="w-5 h-5 text-white" strokeWidth={2.2} />
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold leading-tight text-[15px] truncate">{t("Hospital at Home")}</div>
            <div className="text-white/70 text-[10px] tracking-wider uppercase truncate">
              {showAdmin ? t("Care Beyond Walls") : (onboardingNav?.section ? t(onboardingNav.section) : (role === "hah_nurse" ? t("Nurse Hotline") : t("Workspace")))}
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={toggle}
          data-testid="sidebar-toggle"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="text-white/70 hover:text-white p-1.5 rounded hover:bg-white/10 transition"
        >
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
        {showAdmin && <NavSection title="Admin Console" items={ADMIN_NAV} location={location} collapsed={collapsed} expanded={ex("admin", true)} canSee={canSee} onToggle={() => toggleSection("admin")} t={t} />}
        {showCommand && (
          <NavSection title={COMMAND_NAV.section} items={COMMAND_NAV.items} location={location} collapsed={collapsed} expanded={ex("command", true)} canSee={canSee} onToggle={() => toggleSection("command")} t={t} />
        )}
        {showClinical && (
          <NavSection title={CLINICAL_NAV.section} items={CLINICAL_NAV.items} location={location} collapsed={collapsed} expanded={ex("clinical", true)} canSee={canSee} onToggle={() => toggleSection("clinical")} t={t} />
        )}
        {onboardingNav && role !== "hah_nurse" && (
          <NavSection title={onboardingNav.section} items={onboardingNav.items} location={location} collapsed={collapsed} expanded={ex("onboarding")} canSee={canSee} onToggle={() => toggleSection("onboarding")} t={t} />
        )}
        {showHotline && hotlineNav && (
          <NavSection title="Nurse Hotline" items={hotlineNav.items} location={location} collapsed={collapsed} expanded={ex("hotline")} canSee={canSee} onToggle={() => toggleSection("hotline")} t={t} />
        )}
        {showRpm && (
          <NavSection title={RPM_NAV.section} items={RPM_NAV.items} location={location} collapsed={collapsed} expanded={ex("rpm")} canSee={canSee} onToggle={() => toggleSection("rpm")} t={t} />
        )}
        {showFleet && (
          <NavSection title={FLEET_NAV.section} items={FLEET_NAV.items.filter((i) => !i.adminOnly || role === "admin")} location={location} collapsed={collapsed} expanded={ex("fleet")} canSee={canSee} onToggle={() => toggleSection("fleet")} t={t} />
        )}
        {showTele && (
          <NavSection title={TELECONSULT_NAV.section} items={TELECONSULT_NAV.items} location={location} collapsed={collapsed} expanded={ex("tele")} canSee={canSee} onToggle={() => toggleSection("tele")} t={t} />
        )}

        {!showAdmin && !onboardingNav && !showHotline && (
          <NavSection
            title="Workspace"
            location={location}
            collapsed={collapsed}
            expanded={true}
            canSee={canSee} onToggle={() => {}}
            t={t}
            items={[{ to: "/onboarding", label: "Onboarding Dashboard", icon: LayoutDashboard, exact: true, testId: "nav-ob-dashboard" }]}
          />
        )}
      </nav>

      <div className="px-5 py-4 border-t border-white/10 text-white/70 text-[11px]">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-300" />
          <span>{t("System operational")}</span>
        </div>
        <div className="mt-1 text-white/50">v1.2 • Hotline</div>
      </div>
    </aside>
  );
}
